// ============================================================================
// CHARGE TYPE HOOKS
// ============================================================================

import { useCallback, useEffect, useMemo, useState } from 'react';
import { chargeTypeService } from 'api/services/chargeTypeService';
import type { ChargeType } from 'api/types/chargeType.types';

/**
 * The active charge types for a picker, fetched on every mount (masters change
 * between sessions; nothing is cached across screens). `nameOf` falls back to
 * the code, so a row whose type was since deactivated still reads sensibly.
 */
export const useChargeTypeLookup = (manualOnly = false) => {
  const [types, setTypes] = useState<ChargeType[]>([]);
  const [loading, setLoading] = useState(true);

  const refetch = useCallback(async () => {
    setLoading(true);
    try {
      setTypes(await chargeTypeService.getLookup(manualOnly));
    } catch {
      setTypes([]); // the picker shows empty; the screen's own load reports errors
    } finally {
      setLoading(false);
    }
  }, [manualOnly]);

  useEffect(() => { void refetch(); }, [refetch]);

  const options = useMemo(() => types.map((t) => ({ value: t.chargeCode, label: t.chargeName })), [types]);
  const names = useMemo(() => new Map(types.map((t) => [t.chargeCode, t.chargeName])), [types]);
  const nameOf = useCallback((code?: string | null) => (code ? names.get(code) ?? code : ''), [names]);

  return { types, options, nameOf, loading, refetch };
};
